import {
  encodeAbiParameters,
  keccak256,
  type Address,
  type Hex,
  type WalletClient,
} from "viem";

/**
 * Receipt signing for the inference payment protocol.
 *
 * IMPORTANT: this MUST stay byte-identical to `ComputeUnitMarket._digest` on-chain and to
 * `client/typescript/src/receipt.ts`. It is duplicated here (not imported) only so the frontend is
 * an independently buildable/deployable bundle; if the contract's receipt encoding changes, update
 * all three together.
 */

export interface Receipt {
  user: Address;
  miner: Address;
  brainId: number;
  promptHash: Hex;
  responseHash: Hex;
  amount: bigint;
  expiry: number;
  nonce: bigint;
}

export function receiptDigest(marketAddress: Address, chainId: number, r: Receipt): Hex {
  const encoded = encodeAbiParameters(
    [
      { type: "address" }, // market (address(this))
      { type: "uint256" }, // chainId (block.chainid)
      { type: "address" }, // user
      { type: "address" }, // miner
      { type: "uint256" }, // brainId
      { type: "bytes32" }, // promptHash
      { type: "bytes32" }, // responseHash
      { type: "uint256" }, // amount
      { type: "uint64" }, // expiry
      { type: "uint256" }, // nonce
    ],
    [
      marketAddress,
      BigInt(chainId),
      r.user,
      r.miner,
      BigInt(r.brainId),
      r.promptHash,
      r.responseHash,
      r.amount,
      BigInt(r.expiry),
      r.nonce,
    ],
  );
  return keccak256(encoded);
}

/** Sign a receipt with the connected (injected) wallet. EIP-191 message = the raw digest, matching
 *  the contract's `toEthSignedMessageHash()`. Prompts the user's wallet (personal_sign). */
export async function signReceipt(
  wallet: WalletClient,
  marketAddress: Address,
  chainId: number,
  r: Receipt,
): Promise<Hex> {
  if (!wallet.account) throw new Error("Wallet has no account");
  const digest = receiptDigest(marketAddress, chainId, r);
  return wallet.signMessage({ account: wallet.account, message: { raw: digest } });
}

/** The deterministic pre-auth sentinel responseHash the client signs before any output exists:
 *  keccak256(abi.encodePacked(promptHash, bytes32(nonce))). Must match ComputeUnitMarket.redeem. */
export function preauthSentinel(promptHash: Hex, nonce: bigint): Hex {
  return keccak256(("0x" + promptHash.slice(2) + nonce.toString(16).padStart(64, "0")) as Hex);
}

export function newNonce(): bigint {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let n = 0n;
  for (const b of bytes) n = (n << 8n) | BigInt(b);
  return n;
}

/** Wire format the gateway/miner expect (snake_case, stringified bigints). */
export function serializeReceipt(r: Receipt): Record<string, unknown> {
  return {
    user: r.user,
    miner: r.miner,
    brain_id: r.brainId,
    prompt_hash: r.promptHash,
    response_hash: r.responseHash,
    amount: r.amount.toString(),
    expiry: r.expiry,
    nonce: r.nonce.toString(),
  };
}
